import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit2 } from "react-icons/fi";
import { Currency, type EvalBidDto, type EventValueDto } from "../../../autogen/bff-api";
import { isApiErrorDto } from "../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { useApiError } from "../../../common/errors/useApiError";
import { NumericInput } from "../../../common/input/NumericInput";
import { CommentPopover } from "./CommentPopover";
import { EarlierPriceOverrideNote, StalePriceOverrideIcon } from "./staleScore";

interface Props {
  bid: EvalBidDto;
  readOnly: boolean;
  onSave: (value: number | null, comment: string | undefined, forDeliverySubmittedAt: string | undefined) => Promise<void>;
}

const formatMoney = (v: EventValueDto): string =>
  v.value != null ? `${v.value.toLocaleString("nb-NO")} ${v.currency}` : "–";

export const PriceCell = ({ bid, readOnly, onSave }: Props) => {
  const { t } = useTranslation();
  const overridden = bid.priceOverride != null && supplierGavePrice(bid);
  const effective = bid.effectivePrice;
  const supplier = bid.price;

  return (
    <Flex direction="column" align="center" gap="0.5">
      {overridden && supplier && (
        <Text fontSize="2xs" color="smMuted" textDecoration="line-through" fontFamily="mono">
          {formatMoney(supplier)}
        </Text>
      )}
      <HStack spacing="1">
        <Text
          fontSize="2xs"
          fontFamily="mono"
          fontWeight={bid.priceOverride != null ? "semibold" : "normal"}
          color={bid.priceOverride != null ? "smPrimary" : "smMuted"}
        >
          {effective ? formatMoney(effective) : "–"}
        </Text>
        {bid.priceOverride?.setBeforeLatestDelivery && <StalePriceOverrideIcon />}
        {bid.priceOverride?.comment && <CommentPopover comment={bid.priceOverride.comment} />}
        {!readOnly && <PriceEditPopover bid={bid} onSave={onSave} />}
      </HStack>
      {overridden && (
        <Text fontSize="2xs" color="smPrimary" textTransform="uppercase">
          {t("overridden")}
        </Text>
      )}
      <EarlierPriceOverrideNote bid={bid} />
    </Flex>
  );
};

const PriceEditPopover = ({
  bid,
  onSave,
}: {
  bid: EvalBidDto;
  onSave: (value: number | null, comment: string | undefined, forDeliverySubmittedAt: string | undefined) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const hasBuyerPrice = bid.priceOverride != null;
  const overridesSupplier = supplierGavePrice(bid);
  const [value, setValue] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const deliveryOnScreen = useRef<string | undefined>();

  const reset = () => {
    deliveryOnScreen.current = bid.submittedAt ?? undefined;
    setValue(bid.priceOverride?.value.value ?? null);
    setComment(bid.priceOverride?.comment ?? "");
  };

  const commit = async (nextValue: number | null, nextComment: string, onDone: () => void) => {
    setIsSaving(true);
    try {
      await onSave(nextValue, nextComment || undefined, deliveryOnScreen.current);
      onDone();
    } catch (e) {
      triggerSoft(e);
      if (isDeliveredAgainRefusal(e)) onDone();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popover placement="bottom" isLazy onOpen={reset}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <IconButton
              aria-label={overridesSupplier ? t("Override price") : t("Enter price")}
              icon={<FiEdit2 />}
              size="xs"
              variant="ghost"
              color={hasBuyerPrice ? "smPrimary" : "smMuted"}
            />
          </PopoverTrigger>
          <PopoverContent w="80" maxW="90vw">
            <PopoverArrow />
            <PopoverBody>
              <Stack spacing="2">
                <Text fontSize="xs" color="smMuted" whiteSpace="normal">
                  {t("Set the price used to score this offer")}
                </Text>
                <Flex gap="1" align="center">
                  <Box flex="1">
                    <NumericInput
                      size="sm"
                      numericValue={value}
                      onNumericChange={(v) => setValue(v)}
                      placeholder={t("Price") ?? ""}
                    />
                  </Box>
                  <Text fontSize="sm" color="smMuted">
                    {Currency.Nok}
                  </Text>
                </Flex>
                <Textarea
                  size="sm"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    (overridesSupplier ? t("Reason for the override (optional)") : t("Comment (optional)")) ?? ""
                  }
                />
                <Flex justify="space-between" align="center">
                  {hasBuyerPrice ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      color="smMuted"
                      isDisabled={isSaving}
                      onClick={() => commit(null, "", onClose)}
                    >
                      {overridesSupplier ? t("Reset to supplier price") : t("Remove price")}
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Button
                    size="xs"
                    colorScheme="teal"
                    isLoading={isSaving}
                    onClick={() => commit(value, comment, onClose)}
                  >
                    {t("Save")}
                  </Button>
                </Flex>
              </Stack>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

const isDeliveredAgainRefusal = (e: unknown): boolean =>
  isFetchBaseQueryError(e) &&
  isApiErrorDto(e.data) &&
  e.data.errorCode === "zz_SourcingEventApiErrorCode_BidDeliveredAgainWhileAssessing";

const supplierGavePrice = (bid: EvalBidDto): boolean => bid.price?.value != null;
